import { Sparkles, Home as HomeIcon, Star } from "lucide-react";
import { Link } from "react-router-dom";
import useSiteConfig from "@/lib/useSiteConfig";
import CountUp from "@/components/common/CountUp";

/**
 * SocialValidation — three-pillar proof strip.
 *
 * Two modes:
 *  - Global (default): uses /api/site-config values. Each card links through to the
 *    Guest Love page with a preset filter (5★ rating for the reviews card).
 *  - Property-scoped: pass `property` to show per-unit stats. Cards deep-link to
 *    the Guest Love page filtered to that listing.
 */

const fmtN = (n) => new Intl.NumberFormat("en-IN").format(n || 0);

const computeQuarters = (isoDate) => {
  try {
    const since = new Date(isoDate);
    const now = new Date();
    const months =
      (now.getFullYear() - since.getUTCFullYear()) * 12 +
      (now.getUTCMonth() - since.getUTCMonth());
    return Math.max(1, Math.floor(months / 3));
  } catch { return 1; }
};

const computeYears = (isoDate) => {
  try {
    const since = new Date(isoDate);
    const now = new Date();
    const ms = now - since;
    const years = ms / (1000 * 60 * 60 * 24 * 365.25);
    if (!Number.isFinite(years) || years < 0) return 3;
    return Math.max(3, Math.floor(years));
  } catch { return 3; }
};

const globalPillars = (cfg) => {
  // Anchor the "Superhost since" pillar to a real year so the SSR'd HTML
  // and first paint never show "Since 0 yrs+" to crawlers or users. Falls
  // back to 2022 (when the brand earned the badge) if the date is missing
  // or unparseable.
  const sinceYear = (() => {
    try {
      const y = new Date(cfg.superhost_since || "2022-11-01").getUTCFullYear();
      if (!Number.isFinite(y) || y < 2000 || y > 2100) return 2022;
      return y;
    } catch { return 2022; }
  })();
  // Trust strip uses static values so the rendered HTML always carries
  // real numbers — even before the live aggregation hydrates. CountUp
  // animates from a starting point but the final value is the SSR-safe
  // anchor (so crawlers see "500+", "4.86", etc., not "0").
  const fiveStarValue = Math.max(500, Number(cfg.five_star_reviews || 0));
  const avgRating = (() => {
    const n = Number(cfg.average_rating || 0);
    return n > 0 ? n.toFixed(2) : "4.86";
  })();
  return [
    {
      icon: <Sparkles size={18} className="text-gold" />,
      // Plain text — no animation. Crawlers and humans both see "Since 2022".
      value: <>Since {sinceYear}</>,
      label: "Superhost streak",
      sub: "Airbnb-verified, every quarter",
      href: "https://airbnb.co.in/p/vyagram",
      external: true,
      testId: "social-pillar-link-superhost",
      cta: "View on Airbnb",
    },
    {
      icon: <Star size={18} className="fill-gold text-gold" />,
      value: <CountUp value={fiveStarValue} suffix="+" />,
      label: "Five-star reviews",
      sub: "Read the 5★ reviews from real guests",
      href: "/guest-love?star=5#reviews",
      testId: "social-pillar-link-fivestar",
      cta: "See 5★ reviews",
    },
    {
      icon: <HomeIcon size={18} className="text-gold" />,
      // Render rating as a static string — animating a decimal looks broken
      // and gives crawlers a stale 0 in the DOM during the first paint.
      value: <>{avgRating}</>,
      label: "Average rating",
      sub: "Across hundreds of guest stays",
      href: "/guest-love#reviews",
      testId: "social-pillar-link-rating",
      cta: "Read the reviews",
    },
  ];
};

const propertyPillars = (property, onOpenReviews) => {
  const reviews = Number(property.stats_total_reviews || property.review_count || 0);
  const five = Number(property.stats_five_star || 0);
  const avg = Number(property.stats_avg_rating || property.rating || 0);
  const base = `/guest-love?listing=${property.slug}`;
  const mk = (starFilter) => (onOpenReviews
    ? { onClick: () => onOpenReviews(starFilter) }
    : {});
  return [
    {
      icon: <Sparkles size={18} className="text-gold" />,
      value: <CountUp value={reviews} />,
      label: "Reviews on this unit",
      sub: "From Airbnb, Booking.com and direct guests",
      href: base,
      testId: "social-pillar-link-unit-reviews",
      cta: "Read the reviews",
      ...mk("all"),
    },
    {
      icon: <Star size={18} className="fill-gold text-gold" />,
      value: <CountUp value={five} />,
      label: "Five-star reviews",
      sub: `${five} guests left a perfect 5★`,
      href: `${base}&star=5`,
      testId: "social-pillar-link-unit-fivestar",
      cta: "Filter by 5★",
      ...mk("5"),
    },
    {
      icon: <Star size={18} className="fill-gold text-gold" />,
      value: <CountUp value={avg || 0} decimals={2} />,
      label: "Average rating",
      sub: "Across every approved review",
      href: base,
      testId: "social-pillar-link-unit-avg",
      cta: "See what guests say",
      ...mk("all"),
    },
  ];
};

export default function SocialValidation({ variant = "full", className = "", property = null, onOpenReviews = null }) {
  const cfg = useSiteConfig();
  const pillars = property ? propertyPillars(property, onOpenReviews) : globalPillars(cfg);
  const testId = property ? "social-validation-property" : (variant === "compact" ? "social-validation-compact" : "social-validation");

  if (variant === "compact") {
    return (
      <div
        data-testid={testId}
        className={`flex flex-wrap items-center gap-x-5 gap-y-2 rounded-2xl border border-forest/10 bg-mist/40 px-4 py-3 text-xs text-forest ${className}`}
      >
        {pillars.map((p) => {
          const inner = (
            <>
              {p.icon}
              <span className="font-medium">{p.value}</span>
              <span className="text-noir/60">· {p.label}</span>
            </>
          );
          if (p.onClick) {
            return (
              <button
                key={p.testId || p.label}
                type="button"
                onClick={p.onClick}
                data-testid={p.testId}
                className="inline-flex items-center gap-1.5 hover:text-gold transition-colors"
              >
                {inner}
              </button>
            );
          }
          return p.external ? (
            <a key={p.testId || p.label} href={p.href} target="_blank" rel="noreferrer" data-testid={p.testId} className="inline-flex items-center gap-1.5 hover:text-gold transition-colors">{inner}</a>
          ) : (
            <Link key={p.testId || p.label} to={p.href} data-testid={p.testId} className="inline-flex items-center gap-1.5 hover:text-gold transition-colors">{inner}</Link>
          );
        })}
      </div>
    );
  }

  return (
    <section
      data-testid={testId}
      className={`vyg-section bg-parchment ${className}`}
    >
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-10">
        <p className="vyg-eyebrow text-center">
          {property ? "Trusted by every guest who's stayed" : "Trusted, earned, compounding"}
        </p>
        <h2 className="mt-3 text-center font-display text-3xl sm:text-4xl lg:text-5xl">
          {property
            ? <>Guests on this stay — <span className="font-script text-gold">the proof</span></>
            : <>Guests keep coming back — <span className="font-script text-gold">here's why</span></>}
        </h2>
        <div className="mt-8 grid gap-4 sm:grid-cols-3 sm:gap-6">
          {pillars.map((p) => {
            const cta = p.cta || (p.external ? "View on Airbnb" : "Read the reviews");
            const cls = "group flex flex-col items-start rounded-2xl border border-forest/10 bg-white p-6 shadow-card transition-transform hover:-translate-y-1 hover:shadow-soft sm:p-8";
            const inner = (
              <>
                <span className="inline-flex items-center gap-2 rounded-full border border-forest/10 bg-mist/50 px-3 py-1 text-[11px] uppercase tracking-wider text-forest">
                  {p.icon} {p.label}
                </span>
                <p className="mt-5 font-display text-4xl leading-none text-forest sm:text-5xl">{p.value}</p>
                <p className="mt-2 editorial-body !text-sm text-noir/70">{p.sub}</p>
                <span className="mt-3 inline-flex items-center gap-1 text-xs font-medium uppercase tracking-wider text-amber">
                  {cta} <span aria-hidden>→</span>
                </span>
              </>
            );
            if (p.onClick) {
              return (
                <button key={p.testId || p.label} type="button" onClick={p.onClick} data-testid={p.testId} className={`${cls} text-left`}>{inner}</button>
              );
            }
            return p.external ? (
              <a key={p.testId || p.label} href={p.href} target="_blank" rel="noreferrer" data-testid={p.testId} className={cls}>{inner}</a>
            ) : (
              <Link key={p.testId || p.label} to={p.href} data-testid={p.testId} className={cls}>{inner}</Link>
            );
          })}
        </div>
      </div>
    </section>
  );
}
